import React, { useState } from 'react';
import { useStore, uid } from '../state/store';
import {
  Card, Stat, Chip, GateLight, EvidenceBadge, Banner, Empty, Text, Area, Select, Check,
  Pipeline, SectionHead, ChecklistRow, Modal, KV,
} from '../components/ui';
import { ConstraintForm } from '../components/Forms';
import { DesignatePrimaryModal, ReplacePrimaryModal } from '../components/PipelineForms';
import {
  D001_STAGES, REGULATED_ACTIVITIES, RESPONSIBLE_PARTIES, PARTNER_STAGES, EVIDENCE_LEVELS, EMPTY_COPY,
} from '../lib/constants';
import { candidateScore, scoreProfile, SCORE_CRITERIA, withPrimary, withReplacedPrimary } from '../lib/funnel';
import { fmtDate } from '../lib/dates';

export default function Dubai({ go }) {
  const { state, d, set, add, patch, remove, today } = useStore();
  const d001 = d.d001;
  const funnel = d.funnel;
  const s = state.d001;
  const [modal, setModal] = useState(null);
  const [partnerDraft, setPartnerDraft] = useState(null);

  const setD = (path) => (v) => set(`d001.${path}`, v);

  // Candidates competing to become the first transaction, ranked but not
  // auto-selected. The score structures judgement; the founder decides.
  const candidates = funnel.candidates
    .map((o) => {
      const sc = candidateScore(o);
      const asset = (state.assetCandidates || []).find((a) => a.opportunityId === o.id);
      return { ...o, score: sc.total, scored: sc.scored, asset };
    })
    .sort((a, b) => b.score - a.score);

  const primary = funnel.primary;

  const applyPrimary = ({ candidateId, backupId }) => {
    const chosen = (state.opportunities || []).find((o) => o.id === candidateId);
    set('opportunities', withPrimary(state.opportunities, { candidateId, backupId, today }));
    set('d001.primaryOpportunityId', candidateId);
    set('d001.exists', true);
    if (chosen) {
      const account = (state.accounts || []).find((a) => a.id === chosen.accountId);
      if (account && !String(s.sponsor.company || '').trim()) set('d001.sponsor.company', account.name);
      if (chosen.decisionMaker && !String(s.sponsor.decisionMaker || '').trim())
        set('d001.sponsor.decisionMaker', chosen.decisionMaker);
      const asset = (state.assetCandidates || []).find((a) => a.opportunityId === chosen.id);
      if (asset && !String(s.asset.name || '').trim()) {
        set('d001.asset.name', asset.name);
        set('d001.asset.location', asset.location || '');
        set('d001.asset.namedAssetExists', true);
      }
    }
  };

  const designate = (f) => {
    const chosen = candidates.find((c) => c.id === f.candidateId);
    applyPrimary(f);
    add('d001.primaryHistory', {
      id: uid('ph'),
      date: today,
      opportunityId: f.candidateId,
      name: chosen?.name || '',
      why: f.why,
      backupId: f.backupId,
    });
    add('decisions', {
      id: uid('dec'),
      date: today,
      type: 'D001 selection',
      question: 'Which opportunity becomes Primary D001?',
      options: f.alternatives,
      evidence: `${f.evidence} · #1 risk: ${f.risk}`,
      decision: `${chosen?.name || 'Selected candidate'} designated Primary D001${f.backupId ? `, backup: ${candidates.find((c) => c.id === f.backupId)?.name}` : ', no backup named'}.`,
      reviewDate: f.reviewDate,
    });
  };

  const replacePrimary = (f) => {
    const old = primary;
    const chosen = candidates.find((c) => c.id === f.candidateId);
    if (old) {
      set(
        'opportunities',
        withReplacedPrimary(withPrimary(state.opportunities, { candidateId: f.candidateId, backupId: f.backupId, today }), {
          oldId: old.id,
          disposition: f.disposition,
          today,
        })
      );
      set('d001.primaryOpportunityId', f.candidateId);
    } else {
      applyPrimary(f);
    }
    add('d001.primaryHistory', {
      id: uid('ph'),
      date: today,
      opportunityId: f.candidateId,
      name: chosen?.name || '',
      why: f.reason,
      replaced: old?.name || '',
    });
    add('decisions', {
      id: uid('dec'),
      date: today,
      type: 'D001 selection',
      question: `Replace Primary D001 (${old?.name || 'current'})?`,
      options: `Keep ${old?.name || 'current'} / promote ${chosen?.name || 'candidate'}`,
      evidence: `${f.reason} · ${f.evidence}`,
      decision: `${chosen?.name} becomes Primary D001. ${old?.name || 'Previous primary'} → ${f.disposition}. Impact: ${f.impact}`,
      reviewDate: f.reviewDate,
    });
  };

  if (!d001.exists) {
    return (
      <>
        <div className="page-head">
          <h2>D001</h2>
          <div className="sub">The deep transaction page for whichever opportunity earns the Primary designation.</div>
        </div>
        <Empty title={EMPTY_COPY.noPrimary}>
          {candidates.length > 0
            ? `${candidates.length} candidate(s) are already competing. Compare them below and designate one.`
            : EMPTY_COPY.noCandidates}
          <div className="row" style={{ marginTop: 14 }}>
            <button className="btn primary" disabled={candidates.length === 0} onClick={() => setModal('designate')}>
              Designate Primary D001
            </button>
            <button className="btn" onClick={() => go('pipeline')}>Open Dubai Pipeline</button>
          </div>
        </Empty>

        {candidates.length > 0 && <CandidateComparison candidates={candidates} primaryId="" />}

        {modal === 'designate' && (
          <DesignatePrimaryModal
            candidates={candidates}
            current={null}
            onClose={() => setModal(null)}
            onSave={designate}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <div className="row">
          <h2>DEAL D001</h2>
          <EvidenceBadge level={d001.evidenceLevel} showName />
          {primary && <Chip tone="accent">{primary.name}</Chip>}
          <div className="spacer" />
          <GateLight state={d001.assetGate} label="Asset" />
          <GateLight state={d001.executionGate} label="Execution" />
          <GateLight state={d001.capitalGate} label="Capital" />
        </div>
        <div className="sub">
          The currently highest-quality first-transaction candidate after comparison with the active pipeline —
          not the first opportunity we happened to find.
        </div>
      </div>

      {funnel.counts.backups === 0 && (
        <Banner tone="warn">
          <strong>No backup candidate.</strong> Deal concentration risk is high. Maintain at least one credible backup
          until D001 reaches binding proof.
        </Banner>
      )}

      {/* ------------------------------------------------ the constraint --- */}
      <section className="constraint">
        <div className="eyebrow">The constraint</div>
        <h2>{s.constraint.blocker || 'No constraint set. That is itself a problem.'}</h2>
        <div className="meta">
          <span>Owner: <b>{s.constraint.owner || '—'}</b></span>
          <span className={d001.constraintOverdue ? 'overdue' : ''}>
            Due: <b>{s.constraint.due ? fmtDate(s.constraint.due, { year: true }) : '—'}</b>
            {d001.constraintOverdue ? ' · OVERDUE' : ''}
          </span>
        </div>
        <div className="need">Evidence needed to close it: {s.constraint.evidenceNeeded || '—'}</div>
        <div className="row" style={{ marginTop: 14 }}>
          <button className="btn btn-work" onClick={() => setModal('resolve')}>Resolve</button>
          <button className="btn sm ghost" style={{ color: 'var(--on-dark-dim)' }} onClick={() => setModal('constraint')}>Edit constraint</button>
        </div>
      </section>

      {s.constraintHistory?.length > 0 && (
        <div className="dim tiny" style={{ marginTop: 8 }}>
          {s.constraintHistory.length} constraint(s) resolved: {s.constraintHistory.map((c) => c.blocker).slice(-3).join(' · ')}
        </div>
      )}

      {/* ---------------------------------------------- candidate compare -- */}
      <section className="section">
        <SectionHead
          title="Candidate comparison"
          hint="Multiple candidates, exactly one Primary, one or more backups."
          right={
            <div className="row tight">
              <button className="btn sm" onClick={() => go('pipeline')}>Open pipeline</button>
              <button className="btn sm" disabled={candidates.length < 2} onClick={() => setModal('replace')}>
                Replace Primary D001
              </button>
            </div>
          }
        />
        <CandidateComparison candidates={candidates} primaryId={primary?.id || ''} />
      </section>

      {/* ------------------------------------------------------- pipeline -- */}
      <section className="section">
        <SectionHead
          title="Stage"
          hint="Stage exit requires evidence. Progression is not a mood."
          right={<Chip tone={d001.canAdvance ? 'green' : 'red'}>{d001.canAdvance ? 'Exit evidence met' : 'Exit evidence missing'}</Chip>}
        />
        <Card>
          <Pipeline
            stages={D001_STAGES}
            value={d001.stage}
            canAdvance={d001.canAdvance}
            blockedReason={d001.exitRule ? `Blocked: ${d001.exitRule.text}` : ''}
            onChange={(stage) => {
              set('d001.stage', stage);
              add('d001.stageHistory', { id: uid('st'), date: today, stage });
            }}
          />
          {d001.exitRule && (
            <div style={{ marginTop: 12 }}>
              <Banner tone={d001.canAdvance ? 'ok' : 'warn'}>
                <strong>To leave {d001.stage}:</strong> {d001.exitRule.text}{' '}
                Requires at least <EvidenceBadge level={d001.exitRule.minLevel} /> recorded against Dubai.
                {!d001.canAdvance && ' Record the evidence first — the button stays locked.'}
              </Banner>
            </div>
          )}
        </Card>
      </section>

      {/* --------------------------------------------------------- sponsor - */}
      <section className="section grid g2">
        <Card title="D001 Sponsor">
          <Text label="Sponsor company" value={s.sponsor.company} onChange={setD('sponsor.company')} />
          <Text label="Decision-maker" value={s.sponsor.decisionMaker} onChange={setD('sponsor.decisionMaker')} />
          <Select
            label="Relationship strength"
            value={s.sponsor.relationshipStrength}
            onChange={setD('sponsor.relationshipStrength')}
            options={['', 'Cold', 'Warm intro', 'Existing relationship', 'Trusted']}
          />
          <Area label="Sponsor problem" value={s.sponsor.problem} onChange={setD('sponsor.problem')} rows={2} placeholder="In their words." />
          <Area label="Why now" value={s.sponsor.whyNow} onChange={setD('sponsor.whyNow')} rows={2} />
          <Text label="Current alternative" value={s.sponsor.currentAlternative} onChange={setD('sponsor.currentAlternative')} placeholder="What they do today instead" />
          <Text label="Commercial objective" value={s.sponsor.commercialObjective} onChange={setD('sponsor.commercialObjective')} />
          <Area label="Decision process" value={s.sponsor.decisionProcess} onChange={setD('sponsor.decisionProcess')} rows={2} placeholder="Who signs, what committee, what timeline." />
          <div className="hr" />
          <Check label="Sponsor authority confirmed" checked={s.sponsor.authorityConfirmed} onChange={setD('sponsor.authorityConfirmed')} help="They can commit the asset, not just discuss it." />
          <Check label="Sponsor has agreed a next step" checked={s.sponsor.agreedNextStep} onChange={setD('sponsor.agreedNextStep')} />
        </Card>

        <Card title="D001 Asset">
          <div className="grid g2">
            <Text label="Asset name" value={s.asset.name} onChange={setD('asset.name')} />
            <Text label="Location" value={s.asset.location} onChange={setD('asset.location')} />
            <Text label="Type" value={s.asset.type} onChange={setD('asset.type')} />
            <Text label="Indicative value" value={s.asset.indicativeValue} onChange={setD('asset.indicativeValue')} />
            <Text label="Income" value={s.asset.income} onChange={setD('asset.income')} />
            <Text label="Ownership / title" value={s.asset.ownership} onChange={setD('asset.ownership')} />
            <Text label="Valuation" value={s.asset.valuation} onChange={setD('asset.valuation')} />
            <Text label="Documents available" value={s.asset.documentsAvailable} onChange={setD('asset.documentsAvailable')} />
          </div>
          <Text label="Encumbrances" value={s.asset.encumbrances} onChange={setD('asset.encumbrances')} />
          <Area label="Investment thesis" value={s.asset.thesis} onChange={setD('asset.thesis')} rows={2} />
          <Text label="Repeat-asset potential" value={s.asset.repeatPotential} onChange={setD('asset.repeatPotential')} />
          <div className="hr" />
          <Check label="Named asset exists" checked={s.asset.namedAssetExists} onChange={setD('asset.namedAssetExists')} />
          <Check label="Ownership / title information available" checked={s.asset.titleInfoAvailable} onChange={setD('asset.titleInfoAvailable')} />
          <Check label="Basic economics understood" checked={s.asset.economicsUnderstood} onChange={setD('asset.economicsUnderstood')} />
          <Check label="Asset passes initial screening" checked={s.asset.passesScreen} onChange={setD('asset.passesScreen')} />
        </Card>
      </section>

      {/* ------------------------------------------------------ asset gate - */}
      <section className="section grid g3">
        <Card title="Asset gate" right={<GateLight state={d001.assetGate} label={d001.assetGate.toUpperCase()} />}>
          {d001.assetChecks.map((c) => (
            <ChecklistRow key={c.key} ok={c.ok} label={c.label} />
          ))}
        </Card>
        <Card title="Execution gate" right={<GateLight state={d001.executionGate} label={d001.executionGate.toUpperCase()} />}>
          {d001.executionChecks.map((c) => (
            <ChecklistRow key={c.key} ok={c.ok} label={c.label} detail={c.detail} />
          ))}
          {d001.criticalUnknown.length > 0 && (
            <Banner tone="stop">
              The Execution Gate cannot be green while a critical activity is Unknown.
            </Banner>
          )}
        </Card>
        <Card title="Capital gate" right={<GateLight state={d001.capitalGate} label={d001.capitalGate.toUpperCase()} />}>
          {d001.capitalChecks.map((c) => (
            <ChecklistRow key={c.key} ok={c.ok} label={c.label} detail={c.detail} />
          ))}
        </Card>
      </section>

      {/* ------------------------------------------------ regulated matrix - */}
      <section className="section">
        <SectionHead
          title="Regulated execution"
          hint="Every activity needs a named responsible party. Unknown is a red flag, not a placeholder."
          right={<Chip tone={d001.criticalUnknown.length ? 'red' : 'green'}>{d001.unknown.length} unknown</Chip>}
        />
        <Card flush>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Activity</th>
                  <th style={{ width: 110 }}>Critical</th>
                  <th style={{ width: 220 }}>Responsible party</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {d001.activities.map((a) => (
                  <tr key={a.key} style={a.responsible === 'Unknown' ? { background: 'var(--red-bg)' } : undefined}>
                    <td>{a.label}</td>
                    <td>{a.critical ? <Chip tone="red">Critical</Chip> : <span className="dim tiny">—</span>}</td>
                    <td>
                      <select
                        value={a.responsible}
                        onChange={(e) => set(`d001.regulated.${a.key}.responsible`, e.target.value)}
                      >
                        {RESPONSIBLE_PARTIES.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        value={a.note}
                        placeholder="Which entity, under which permission?"
                        onChange={(e) => set(`d001.regulated.${a.key}.note`, e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      {/* -------------------------------------------------------- partners - */}
      <section className="section">
        <SectionHead
          title="Execution partner pipeline"
          hint="Without a licensed partner at Pilot Ready, D001 cannot execute."
          right={<button className="btn sm" onClick={() => setPartnerDraft(blankPartner())}>Add partner</button>}
        />
        {d001.partners.length === 0 ? (
          <Empty title={EMPTY_COPY.partners}>Identify who holds the permissions you do not.</Empty>
        ) : (
          <div className="grid g2">
            {d001.partners.map((p) => (
              <Card
                key={p.id}
                title={p.org || 'Unnamed partner'}
                right={
                  <div className="row tight">
                    <EvidenceBadge level={p.evidenceLevel || 'E0'} />
                    <button className="btn sm ghost" onClick={() => setPartnerDraft(p)}>Edit</button>
                  </div>
                }
              >
                <div style={{ marginBottom: 10 }}>
                  <Pipeline
                    stages={PARTNER_STAGES}
                    value={p.stage}
                    onChange={(stage) => patch('d001.partners', p.id, { stage })}
                  />
                </div>
                <KV
                  items={[
                    ['Permission / licence', p.permission],
                    ['Relevant activities', p.activities],
                    ['Limitations', p.limitations],
                    ['Client ownership', p.clientOwnership],
                    ['Data ownership', p.dataOwnership],
                    ['Integration', p.integration],
                    ['Economics', p.economics],
                    ['SLA', p.sla],
                    ['Next action', p.nextAction],
                  ]}
                />
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* --------------------------------------------------------- capital - */}
      <section className="section grid g-1-2">
        <Card title="Capital & distribution">
          <Text label="Target investor segment" value={s.capital.targetSegment} onChange={setD('capital.targetSegment')} />
          <Text label="Expected ticket" value={s.capital.expectedTicket} onChange={setD('capital.expectedTicket')} />
          <Area label="Capital acceptance criteria" value={s.capital.acceptanceCriteria} onChange={setD('capital.acceptanceCriteria')} rows={2} />
          <Area label="Distribution limitations" value={s.capital.distributionLimitations} onChange={setD('capital.distributionLimitations')} rows={2} />
          <Area label="Deal-specific interest" value={s.capital.dealInterest} onChange={setD('capital.dealInterest')} rows={2} />
        </Card>

        <Card
          title="Channel conversations"
          right={
            <button
              className="btn sm"
              onClick={() => add('d001.capital.conversations', { id: uid('cc'), name: '', date: today, note: '', credible: true, acceptanceSignal: false })}
            >
              Add conversation
            </button>
          }
          flush
        >
          {s.capital.conversations.length === 0 ? (
            <div style={{ padding: 14 }}>
              <Empty title="No capital conversations recorded.">
                The gate needs two credible conversations and one D001-specific acceptance signal.
              </Empty>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th style={{ width: 130 }}>Date</th>
                    <th>Note</th>
                    <th style={{ width: 150 }}>D001 acceptance</th>
                    <th className="actions" />
                  </tr>
                </thead>
                <tbody>
                  {s.capital.conversations.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <input type="text" value={c.name} placeholder="Institution" onChange={(e) => patch('d001.capital.conversations', c.id, { name: e.target.value })} />
                      </td>
                      <td>
                        <input type="date" value={c.date} onChange={(e) => patch('d001.capital.conversations', c.id, { date: e.target.value })} />
                      </td>
                      <td>
                        <input type="text" value={c.note} onChange={(e) => patch('d001.capital.conversations', c.id, { note: e.target.value })} />
                      </td>
                      <td>
                        <label className="check" style={{ padding: 0 }}>
                          <input type="checkbox" checked={!!c.acceptanceSignal} onChange={(e) => patch('d001.capital.conversations', c.id, { acceptanceSignal: e.target.checked })} />
                          <span className="tiny">Signal recorded</span>
                        </label>
                      </td>
                      <td className="actions">
                        <button className="btn sm ghost" onClick={() => remove('d001.capital.conversations', c.id)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </section>

      {modal === 'constraint' && (
        <ConstraintForm current={s.constraint} onClose={() => setModal(null)} onSave={(c) => set('d001.constraint', c)} />
      )}
      {modal === 'resolve' && (
        <ResolveConstraint
          current={s.constraint}
          onClose={() => setModal(null)}
          onResolve={(next) => {
            add('d001.constraintHistory', { ...s.constraint, id: uid('ch'), resolvedOn: today });
            set('d001.constraint', next);
          }}
        />
      )}
      {modal === 'designate' && (
        <DesignatePrimaryModal candidates={candidates} current={primary} onClose={() => setModal(null)} onSave={designate} />
      )}
      {modal === 'replace' && (
        <ReplacePrimaryModal current={primary} candidates={candidates} onClose={() => setModal(null)} onSave={replacePrimary} />
      )}
      {partnerDraft && (
        <PartnerModal
          draft={partnerDraft}
          onClose={() => setPartnerDraft(null)}
          onSave={(p) => {
            const exists = (s.partners || []).some((x) => x.id === p.id);
            if (exists) patch('d001.partners', p.id, p);
            else add('d001.partners', p);
          }}
          onDelete={(id) => remove('d001.partners', id)}
        />
      )}
    </>
  );
}

// Up to five candidates side by side. Highlights Primary and backup, and shows
// what each one is missing rather than only what it scores.
function CandidateComparison({ candidates, primaryId }) {
  const shown = candidates.slice(0, 5);
  if (shown.length === 0) {
    return <Empty title={EMPTY_COPY.noCandidates}>Mark an opportunity as a D001 candidate from the pipeline.</Empty>;
  }
  return (
    <Card flush>
      <div className="table-wrap">
        <table className="table cand-table">
          <thead>
            <tr>
              <th>Sponsor / opportunity</th>
              <th style={{ width: 150 }}>Asset</th>
              <th style={{ width: 60 }}>E</th>
              <th style={{ width: 80 }}>Score</th>
              {SCORE_CRITERIA.slice(0, 6).map((c) => (
                <th key={c.key} className="num" style={{ width: 62 }} title={c.help}>
                  {shortLabel(c.label)}
                </th>
              ))}
              <th>Current blocker</th>
              <th>Next action</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((c) => {
              const profile = scoreProfile(c);
              const isPrimary = c.id === primaryId;
              return (
                <tr key={c.id} className={isPrimary ? 'cand-row-primary' : c.backupD001 ? 'cand-row-backup' : ''}>
                  <td>
                    <div className="row tight">
                      <strong>{c.accountName || 'Unnamed sponsor'}</strong>
                      {isPrimary && <Chip tone="accent">Primary</Chip>}
                      {!isPrimary && c.backupD001 && <Chip>Backup</Chip>}
                    </div>
                    <div className="dim tiny">{c.name}</div>
                    {profile.missing.length > 0 && (
                      <div className="dim tiny">Missing evidence: {profile.missing.slice(0, 3).map((m) => m.label.toLowerCase()).join(', ')}{profile.missing.length > 3 ? '…' : ''}</div>
                    )}
                  </td>
                  <td className="tiny">{c.asset?.name || <span className="dim">none</span>}</td>
                  <td><EvidenceBadge level={c.evidenceLevel || 'E0'} /></td>
                  <td className="score">{c.scored ? `${c.score}/50` : '—'}</td>
                  {SCORE_CRITERIA.slice(0, 6).map((crit) => {
                    const v = profile.rows.find((r) => r.key === crit.key)?.value || 0;
                    return (
                      <td key={crit.key} className="num" style={{ color: v === 0 ? 'var(--ink-4)' : v <= 2 ? 'var(--red)' : v >= 4 ? 'var(--green)' : undefined }}>
                        {v || '—'}
                      </td>
                    );
                  })}
                  <td className="tiny">{profile.risks[0] ? `Weakest: ${profile.risks[0].label.toLowerCase()}` : <span className="dim">no scored weakness</span>}</td>
                  <td className="tiny">{c.nextAction || <span className="dim">none set</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ padding: '10px 14px', borderTop: '1px solid var(--line-2)' }}>
        <span className="dim tiny">
          Columns shown: sponsor authority, asset readiness, title clarity, commercial urgency, regulatory simplicity,
          execution partner fit. Full scoring lives on each opportunity. Do not designate a Primary because a number is
          higher — use the score to structure the judgement.
        </span>
      </div>
    </Card>
  );
}

function shortLabel(label) {
  return label
    .replace(' / ownership clarity', ' clarity')
    .replace('Sponsor authority', 'Authority')
    .replace('Asset readiness', 'Readiness')
    .replace('Title clarity', 'Title')
    .replace('Commercial urgency', 'Urgency')
    .replace('Regulatory simplicity', 'Reg.')
    .replace('Execution partner fit', 'Partner');
}

function blankPartner() {
  return {
    id: uid('pt'),
    org: '',
    stage: 'Identified',
    permission: '',
    activities: '',
    limitations: '',
    clientOwnership: '',
    dataOwnership: '',
    integration: '',
    economics: '',
    sla: '',
    nextAction: '',
    evidenceLevel: 'E0',
    isNew: true,
  };
}

function PartnerModal({ draft, onClose, onSave, onDelete }) {
  const [p, setP] = useState(draft);
  const set = (k) => (v) => setP((x) => ({ ...x, [k]: v }));
  return (
    <Modal
      title={draft.isNew ? 'Add execution partner' : draft.org || 'Partner'}
      onClose={onClose}
      wide
      footer={
        <>
          {!draft.isNew && (
            <button className="btn danger" onClick={() => { onDelete(p.id); onClose(); }}>Delete</button>
          )}
          <div className="spacer" />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={() => { onSave({ ...p, isNew: false }); onClose(); }}>Save</button>
        </>
      }
    >
      <div className="grid g2">
        <Text label="Organization" value={p.org} onChange={set('org')} />
        <Select label="Stage" value={p.stage} onChange={set('stage')} options={PARTNER_STAGES} />
        <Text label="Permission / licence" value={p.permission} onChange={set('permission')} />
        <Select label="Evidence level" value={p.evidenceLevel} onChange={set('evidenceLevel')} options={EVIDENCE_LEVELS.map((e) => ({ value: e.id, label: `${e.id} — ${e.name}` }))} />
      </div>
      <Area label="Relevant activities" value={p.activities} onChange={set('activities')} rows={2} />
      <Area label="Limitations" value={p.limitations} onChange={set('limitations')} rows={2} />
      <div className="grid g2">
        <Text label="Client ownership" value={p.clientOwnership} onChange={set('clientOwnership')} />
        <Text label="Data ownership" value={p.dataOwnership} onChange={set('dataOwnership')} />
        <Text label="Integration" value={p.integration} onChange={set('integration')} />
        <Text label="Economics" value={p.economics} onChange={set('economics')} />
        <Text label="SLA" value={p.sla} onChange={set('sla')} />
        <Text label="Next action" value={p.nextAction} onChange={set('nextAction')} />
      </div>
    </Modal>
  );
}

function ResolveConstraint({ current, onClose, onResolve }) {
  const [next, setNext] = useState({ blocker: '', owner: 'Founder', due: '', evidenceNeeded: '' });
  const set = (k) => (v) => setNext((x) => ({ ...x, [k]: v }));
  return (
    <Modal
      title="Constraint resolved"
      sub={current.blocker}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!next.blocker.trim()} onClick={() => { onResolve(next); onClose(); }}>
            Set new constraint
          </button>
        </>
      }
    >
      <Banner>What is now the next largest constraint? There is always one.</Banner>
      <div style={{ marginTop: 14 }}>
        <Area label="Next constraint" value={next.blocker} onChange={set('blocker')} rows={2} />
        <div className="grid g2">
          <Text label="Owner" value={next.owner} onChange={set('owner')} />
          <Text label="Due" type="date" value={next.due} onChange={set('due')} />
        </div>
        <Area label="Evidence needed to close it" value={next.evidenceNeeded} onChange={set('evidenceNeeded')} rows={2} />
      </div>
    </Modal>
  );
}
